// Access model v2 helpers shared by the Permissions page, the Users dialog
// and the Explain panel. effective = ((audiences ∪ role grants) − role revokes ∪ user grants) − user revokes

export const STATE_LABEL = {
  none: "Not granted",
  inherited: "Inherited from audience defaults",
  granted: "Granted here",
  revoked: "Revoked here",
};

export function cellState({ inherited, granted, revoked }) {
  if (revoked) return "revoked";
  if (granted) return "granted";
  if (inherited) return "inherited";
  return "none";
}

// Click cycle: none → granted → none · inherited → revoked → inherited
export function nextTweak(state) {
  if (state === "none") return { granted: true, revoked: false };
  if (state === "granted") return { granted: false, revoked: false };
  if (state === "inherited") return { granted: false, revoked: true };
  return { granted: false, revoked: false };
}

export function groupCatalog(catalog) {
  if (!catalog?.modules) return [];
  return (catalog.groups || []).map((g) => ({
    ...g,
    modules: catalog.modules.filter((m) => m.group === g.id),
  })).filter((g) => g.modules.length);
}

function moduleText(m) {
  return [m.label, m.id, ...(m.apps || []).map((a) => a.name || "")].join(" ").toLowerCase();
}

// A query hits a module by name/id/app, or only some of its actions ("export", "kb.view").
function narrowModule(m, needle) {
  if (!needle || moduleText(m).includes(needle)) return m;
  const actions = (m.actions || []).filter((a) => a.toLowerCase().includes(needle)
    || `${m.id}.${a}`.toLowerCase().includes(needle));
  return actions.length ? { ...m, actions } : null;
}

export function filterGroups(groups, { group = "", q = "", modules = [] } = {}) {
  const needle = q.trim().toLowerCase();
  const picked = new Set(modules || []);
  return groups
    .filter((g) => !group || g.id === group)
    .map((g) => ({
      ...g,
      modules: g.modules
        .filter((m) => !picked.size || picked.has(m.id))
        .map((m) => narrowModule(m, needle))
        .filter(Boolean),
    }))
    .filter((g) => g.modules.length);
}

export function audiencesForRole(role, audiences) {
  if (!role) return [];
  return (audiences || []).filter((a) => a.active !== false && a.audience_id !== "super"
    && ((a.base_roles || []).includes(role.base_role) || (a.base_roles || []).includes(role.role_id)));
}

// Union of the matching audiences' defaults, with the audience labels behind each permission.
export function roleBaseline(role, audiences) {
  const sources = new Map();
  audiencesForRole(role, audiences).forEach((a) => {
    (a.permissions || []).forEach((p) => {
      if (!sources.has(p)) sources.set(p, []);
      sources.get(p).push(a.label || a.audience_id);
    });
  });
  return sources;
}

export function roleEffective(role, audiences) {
  if (!role) return new Set();
  if (role.role_id === "super_admin") return new Set(role.permissions || []);
  const out = new Set(roleBaseline(role, audiences).keys());
  (role.permissions || []).forEach((p) => out.add(p));
  (role.revoked || []).forEach((p) => out.delete(p));
  return out;
}

export function roleCellState(role, perm, audiences) {
  if (!role) return "none";
  if (role.role_id === "super_admin") return "inherited";
  return cellState({
    inherited: roleBaseline(role, audiences).has(perm),
    granted: (role.permissions || []).includes(perm),
    revoked: (role.revoked || []).includes(perm),
  });
}

export function sourceLabel(src, audienceLabels = {}) {
  if (src === "super_admin") return "Super Admin";
  if (src === "role") return "Role";
  if (src === "user") return "User";
  if (src?.startsWith("audience:")) {
    const id = src.slice("audience:".length);
    return audienceLabels[id] || id;
  }
  return src;
}
